import type { ChangeEventHandler, FC, MouseEventHandler } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Picture } from '@ui';
import debounce from 'lodash/debounce';
import { getURLProps } from '@utils/setPagesUrl';
import { useLocation } from '@compat/router';
import icons from '@icons';
import './DelayInput.scss';
import clsx from 'clsx';
import { observer } from 'mobx-react';

const {
  close: { darkGrey: closeDGOutlined, red: closeRedOutined },
  search: { darkGrey: searchDGOutlined, red: searchRedOutliend }
} = icons.Outline;

interface DelayInputProps {
  changeParams: (param: string, id: string) => void;
  delay?: number;
  placeholder: string;
  size?: 'small' | 'medium' | 'large';
}

const DelayInput: FC<DelayInputProps> = observer(
  ({ changeParams, delay, placeholder, size = 'medium' }) => {
    const location = useLocation();
    // getURLProps сам раскодирует значение из pathname (setPagesUrl.ts).
    // Своего decodeURI здесь больше нет: на запросе с литеральным «%»
    // (`50%` → `/r/search/50%25`) он падал бы URIError на уже готовой строке.
    const [search, setSearch] = useState(
      () => getURLProps({ pathname: location?.pathname }).search
    );

    const changeParamsRef = useRef(changeParams);
    changeParamsRef.current = changeParams;

    const delayedCallback = useMemo(
      () =>
        debounce((param: string, id: string) => {
          changeParamsRef.current(param, id);
        }, delay ?? 1000),
      [delay]
    );

    useEffect(() => () => delayedCallback.cancel(), [delayedCallback]);

    const searchComments: ChangeEventHandler<HTMLInputElement> = event => {
      event.persist();
      setSearch(event.target.value);
      delayedCallback('search', event.target.value);
    };

    const clearInput: MouseEventHandler<HTMLElement> = (e): void => {
      e.persist();
      setSearch('');
      delayedCallback('search', '');
    };

    return (
      <div
        className={clsx(
          'delay-input',
          size === 'small' && 'delay-input--small',
          size === 'large' && 'delay-input--large'
        )}
      >
        <input
          type='search'
          autoComplete='off'
          placeholder={placeholder || 'Поиск'}
          value={search}
          onChange={searchComments}
        />
        <div className='delay-input__wrapper'>
          {search && (
            <Picture
              icon={closeDGOutlined.x2}
              iconHover={closeRedOutined.x2}
              height='24'
              width='24'
              onClick={clearInput}
            />
          )}
          {!search && (
            <Picture
              pictureClassName='delay-input__btn'
              icon={search ? searchRedOutliend : searchDGOutlined}
              height='24'
              width='24'
            />
          )}
        </div>
      </div>
    );
  }
);

export default DelayInput;
